// src/pages/FaqPublicResults/FaqPublicResults.jsx
import React, { useMemo, useRef, useState } from "react";

import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";

import { HELP_SECTIONS } from "./faqPublicResultsData";

import HelpHero from "./components/HelpHero";
import HelpToolbar from "./components/HelpToolbar";
import HelpSidebar from "./components/HelpSidebar";
import HelpAccordion from "./components/HelpAccordion";

const PAGE =
  "min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50";

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

export default function FaqPublicResults() {
  useScrollToTop();

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [openIds, setOpenIds] = useState(() => new Set(["resultats-publics"]));

  const sectionRefs = useRef({});

  const filteredSections = useMemo(() => {
    const query = normalizeText(search.trim());

    return HELP_SECTIONS.filter((section) => {
      const matchesCategory =
        category === "all" || section.category === category;

      if (!matchesCategory) return false;

      if (!query) return true;

      const content = [
        section.title,
        section.eyebrow,
        section.summary,
        ...section.blocks.flatMap((block) => [
          block.title,
          block.warning,
          ...block.items,
        ]),
      ]
        .map(normalizeText)
        .join(" ");

      return content.includes(query);
    });
  }, [search, category]);

  const toggleSection = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const expandAll = () => {
    setOpenIds(new Set(filteredSections.map((section) => section.id)));
  };

  const collapseAll = () => {
    setOpenIds(new Set());
  };

  const reset = () => {
    setSearch("");
    setCategory("all");
    setOpenIds(new Set(["resultats-publics"]));
  };

  const goToSection = (id) => {
    setOpenIds((prev) => new Set([...prev, id]));

    setTimeout(() => {
      sectionRefs.current[id]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
  };

  return (
    <div className={PAGE}>
      <ScrollIndicator />

      <main className="mx-auto w-full max-w-[1280px] px-3 pb-12 pt-[120px] sm:px-5 md:pt-[145px]">
        <div className="space-y-6">
          <HelpHero
            totalSections={HELP_SECTIONS.length}
            visibleSections={filteredSections.length}
          />

          <HelpToolbar
            search={search}
            setSearch={setSearch}
            category={category}
            setCategory={setCategory}
            onExpandAll={expandAll}
            onCollapseAll={collapseAll}
            onReset={reset}
          />

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[280px_minmax(0,1fr)]">
            <HelpSidebar
              sections={filteredSections}
              activeCategory={category}
              setActiveCategory={setCategory}
              onGoToSection={goToSection}
            />

            <section className="space-y-4">
              {filteredSections.length === 0 ? (
                <EmptyState onReset={reset} />
              ) : (
                filteredSections.map((section) => (
                  <HelpAccordion
                    key={section.id}
                    section={section}
                    isOpen={openIds.has(section.id)}
                    onToggle={() => toggleSection(section.id)}
                    sectionRef={(node) => {
                      sectionRefs.current[section.id] = node;
                    }}
                  />
                ))
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

function EmptyState({ onReset }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[28px] border border-dashed border-slate-300 bg-white px-5 py-16 text-center dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-slate-100 text-3xl dark:bg-slate-800">
        🔎
      </div>

      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
        Aucun résultat trouvé
      </h3>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        Essayez avec un autre mot-clé ou réinitialisez les filtres.
      </p>

      <button
        type="button"
        onClick={onReset}
        className="mt-5 inline-flex items-center justify-center rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
      >
        Réinitialiser la recherche
      </button>
    </div>
  );
}